import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class WidgetErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Handle "Script error" (CORS errors) gracefully
    if (error.message === 'Script error.' || error.message === '') {
      console.warn('[WidgetErrorBoundary] CORS script error detected - suppressing to prevent page crash');
      // Don't call onError for CORS errors as they're expected
      return;
    }
    
    console.error('[WidgetErrorBoundary] Widget error caught:', error, errorInfo);
    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo);
      } catch (callbackError) {
        // Prevent error callback from throwing
        console.error('[WidgetErrorBoundary] Error in onError callback:', callbackError);
      }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            padding: 3,
            textAlign: 'center',
            bgcolor: 'error.light',
            borderRadius: 2,
            border: 1,
            borderColor: 'error.main',
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Widget Error
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {this.state.error?.message || 'An error occurred while rendering the widget'}
          </Typography>
          <Button variant="outlined" color="error" onClick={this.handleReset}>
            Try Again
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

